// Upload a learning-module asset (screenshot or document) to S3 via a presigned PUT URL.
//
// Mirrors users/utils/avatarUpload.ts: request a presigned URL from the requestUploadUrl
// Netlify function, PUT the file straight to S3, and return the permanent URL (presigned
// URL minus its query string). The function appends the extension (derived from the file's
// MIME type) to fileName, so fileName is passed WITHOUT one.
//
// NOTE (ops): assets go in the shared documents bucket under a `hopdrive-learning-modules/`
// folder. The bucket is env-driven via config.awsDocumentsBucket — `documents-test.hopdrive.com`
// in test, `documents.hopdrive.com` in prod — so no code change is needed at deploy time.

import axios from 'axios';
import config from '@config';
import { authService } from '../../auth/services/authService';
import { genId } from './ids';

// Env-aware documents bucket (documents-test.hopdrive.com / documents.hopdrive.com).
const BUCKET = config.awsDocumentsBucket;
const ASSET_FOLDER = 'hopdrive-learning-modules';

/**
 * Upload one file and return its permanent S3 URL.
 *
 * @param file - the File to upload (image or document)
 * @param moduleKey - the module's stable key, used to group its assets in a subfolder
 *   (falls back to 'unkeyed' for a module that hasn't been given a key yet)
 */
// The permanent URL is the presigned URL minus its query string, and resolveAssetUrl later
// re-extracts the S3 key from that URL — so the folder must survive a URL round-trip unmangled.
// Only a slug-format key (the same rule the editor's canSave enforces) is used verbatim; a key
// mid-edit that wouldn't validate (e.g. contains a space) groups under 'unkeyed' instead of
// minting an object whose stored URL percent-encodes to a different key than was uploaded.
const SAFE_FOLDER_RE = /^[a-z0-9][a-z0-9_-]*$/i;

export async function uploadModuleAsset(file: File, moduleKey: string): Promise<string> {
  const token = await authService.getAccessToken();
  const trimmedKey = moduleKey.trim();
  const folder = SAFE_FOLDER_RE.test(trimmedKey) ? trimmedKey : 'unkeyed';
  // Key: hopdrive-learning-modules/<moduleKey>/<id>. No extension — requestUploadUrl
  // appends it from the file's MIME type.
  const fileName = `${ASSET_FOLDER}/${folder}/${genId('asset')}`;

  const { data } = await axios.post(
    '/.netlify/functions/requestUploadUrl',
    {
      fileName,
      fileType: file.type,
      bucketName: BUCKET,
    },
    {
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    },
  );

  const { uploadURL } = data;

  await axios.put(uploadURL, file, {
    headers: { 'Content-Type': file.type },
  });

  // Strip query params to get the permanent URL.
  return uploadURL.split('?')[0];
}

// Our documents buckets are PRIVATE (documents-test.hopdrive.com / documents.hopdrive.com),
// so a stored permanent URL 403s if opened directly. Match those and swap the key. Stored URLs
// come in two shapes: the bucket domain as the host, or path-style S3
// (https://s3[.region].amazonaws.com/<bucket>/<key> — see the certifications document flow).
// Anchored to those two hosts so an arbitrary URL merely *containing* the bucket domain in its
// path (e.g. https://evil.com/documents.hopdrive.com/x) isn't classified as a private asset.
const DOCUMENTS_BUCKET_RE =
  /^https?:\/\/(?:s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com\/)?(documents(?:-[a-z0-9]+)?\.hopdrive\.com)\/(.+)$/i;

/**
 * Whether a URL points at one of our private documents buckets. Such URLs 403 if opened directly
 * (they only work via a short-lived presigned URL from resolveAssetUrl), so callers that can't
 * resolve one for display — e.g. a PDF export — must NOT surface it as a clickable/plain link.
 */
export function isPrivateAssetUrl(url: string | null | undefined): boolean {
  return !!url && DOCUMENTS_BUCKET_RE.test(url);
}

/**
 * Resolve a stored asset URL to something the browser can actually load.
 *
 * If the URL points at one of our private documents buckets, request a short-lived presigned
 * GET URL (via requestDownloadUrl, mirroring the certifications document flow). Any other URL
 * (e.g. a pasted public link, placehold.co, a YouTube thumbnail) is returned unchanged.
 *
 * Note: presigned URLs expire (~5 min), so resolve at display/click time, not once up front.
 */
export async function resolveAssetUrl(url: string | null | undefined): Promise<string> {
  if (!url) return '';
  const match = url.match(DOCUMENTS_BUCKET_RE);
  if (!match) {
    // External/public URL — use as-is, but only if it's an http(s) (or same-origin relative) URL.
    // attachment_url is free text an author types, so a `javascript:`/`data:` scheme here would be
    // navigated to when a viewer clicks "Download document"; reject anything else.
    try {
      const { protocol } = new URL(url, window.location.href);
      return protocol === 'http:' || protocol === 'https:' ? url : '';
    } catch {
      return '';
    }
  }
  const bucketName = match[1];
  // The key was lifted from a URL path, so it may be percent-encoded; S3 wants the literal
  // object key (e.g. `a%20b` in the URL names an object whose key contains a real space).
  const rawKey = match[2].split('?')[0];
  let key: string;
  try {
    key = decodeURIComponent(rawKey);
  } catch {
    key = rawKey; // malformed escape sequence — pass through unchanged
  }

  const token = await authService.getAccessToken();
  const { data } = await axios.post(
    '/.netlify/functions/requestDownloadUrl',
    { bucketName, key },
    { headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` } },
  );
  return data.downloadURL as string;
}
